import React, { useCallback, useEffect, useState } from 'react';
import { FaEdit, FaSave } from 'react-icons/fa';
import { TbCancel } from 'react-icons/tb';
import { clientService } from '../../services/clientService';
import { notifyError, notifySuccess } from '../../utils/notifications';
import GenericTable from "../GenericTable";

const ClientDetails = ({ clientData, workshopID, onViewVehicle, onClose, onUpdate }) => {
    const [vehicles, setVehicles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [originalData, setOriginalData] = useState({});

    useEffect(() => {
        if (clientData?.id && workshopID) {
            fetchClientDetails(clientData.id);
        }
        setEditedData(clientData);
        setOriginalData(clientData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientData, workshopID]);

    const fetchClientDetails = async (clientId) => {
        setIsLoading(true);
        try {
            await Promise.all([
                fetchClientVehicles(clientId)
            ]);
        } catch (error) {
            console.error('Error al cargar detalles del cliente: ', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchClientVehicles = async (clientId) => {
        try {
            const data = await clientService.getClientVehicles(clientId);
            setVehicles(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al obtener vehículos del cliente: ', error);
            setVehicles([]);
        }
    };

    const handleViewVehicle = useCallback((vehicleData) => {
        if (onViewVehicle) {
            onViewVehicle(vehicleData);
        }
    }, [onViewVehicle]);

    const handleEdit = () => {
        setIsEditMode(true);
    };

    const handleCancel = () => {
        setEditedData(originalData);
        setIsEditMode(false);
    };

    const handleSave = async () => {
        try {
            const payload = {
                name: editedData.client_name || '',
                first_lastname: editedData.first_lastname || '',
                second_lastname: editedData.second_lastname || '',
                email: editedData.email || '',
                address: editedData.address || '',
                phone: editedData.phone || '',
                RFC: editedData.rfc || '',
                company_name: editedData.company_name || '',
                second_phone: editedData.second_phone || '',
            };

            await clientService.updateClient(clientData.id, payload);
            setOriginalData(editedData);
            onUpdate(clientData.id, payload);
            setIsEditMode(false);
            notifySuccess('Cliente actualizado correctamente.');
            onClose();
        } catch (error) {
            console.error('Error al actualizar cliente: ', error);
            notifyError('No se pudo actualizar el cliente.');
        }
    };

    const columnsVehicles = React.useMemo(() => [
        { Header: 'Marca', accessor: 'marca' },
        { Header: 'Submarca', accessor: 'sub_marca' },
        { Header: 'Modelo', accessor: 'modelo' },
        { Header: 'Matrícula', accessor: 'matricula' },
        { Header: 'Color', accessor: 'color' },
        {
            Header: 'Acciones',
            accessor: 'acciones',
            Cell: ({ row }) => (
                <span
                    className='action-text'
                    onClick={() => handleViewVehicle(row.original)}
                >
                    Ver detalles
                </span>
            )
        }
    ], [handleViewVehicle]);

    if (isLoading) {
        return (
            <div className="loading-container">
                <p>Cargando información del cliente...</p>
            </div>
        );
    }

    return (
        <div className="client-details-content">
            {/* Información General */}
            <section className="client-info-section">
                <div className="section-header-with-button sticky-section-header">
                    <h3>Información General</h3>
                    <div className="header-actions">
                        {!isEditMode && (
                            <button className="btn-edit-inline" onClick={handleEdit}>
                                <FaEdit /> Editar
                            </button>
                        )}
                        {isEditMode && (
                            <>
                                <button className="btn-save" onClick={handleSave}>
                                    <FaSave /> Guardar
                                </button>
                                <button className="btn-cancel" onClick={handleCancel}>
                                    <TbCancel /> Cancelar
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <div className="info-grid-editable">
                    <div className="info-field">
                        <label>Nombre(s):</label>
                        <input
                            type="text"
                            placeholder="N/A"
                            value={editedData.client_name || ''}
                            onChange={(e) => setEditedData({ ...editedData, client_name: e.target.value })}
                            disabled={!isEditMode}
                            className={isEditMode ? 'input-editable' : 'input-readonly'}
                        />
                    </div>
                    <div className="info-field">
                        <label>Apellido Paterno:</label>
                        <input
                            type="text"
                            placeholder="N/A"
                            value={editedData.first_lastname || ''}
                            onChange={(e) => setEditedData({ ...editedData, first_lastname: e.target.value })}
                            disabled={!isEditMode}
                            className={isEditMode ? 'input-editable' : 'input-readonly'}
                        />
                    </div>
                    <div className="info-field">
                        <label>Apellido Materno:</label>
                        <input
                            type="text"
                            placeholder="N/A"
                            value={editedData.second_lastname || ''}
                            onChange={(e) => setEditedData({ ...editedData, second_lastname: e.target.value })}
                            disabled={!isEditMode}
                            className={isEditMode ? 'input-editable' : 'input-readonly'}
                        />
                    </div>
                    <div className="info-field">
                        <label>Empresa:</label>
                        <input
                            type="text"
                            placeholder="N/A"
                            value={editedData.company_name || ''}
                            onChange={(e) => setEditedData({ ...editedData, company_name: e.target.value })}
                            disabled={!isEditMode}
                            className={isEditMode ? 'input-editable' : 'input-readonly'}
                        />
                    </div>
                    <div className="info-field">
                        <label>Dirección:</label>
                        <input
                            type="text"
                            placeholder="N/A"
                            value={editedData.address || ''}
                            onChange={(e) => setEditedData({ ...editedData, address: e.target.value })}
                            disabled={!isEditMode}
                            className={isEditMode ? 'input-editable' : 'input-readonly'}
                        />
                    </div>
                    <div className="info-field">
                        <label>Teléfono:</label>
                        <input
                            type="text"
                            placeholder="N/A"
                            value={editedData.phone || ''}
                            onChange={(e) => setEditedData({ ...editedData, phone: e.target.value })}
                            disabled={!isEditMode}
                            className={isEditMode ? 'input-editable' : 'input-readonly'}
                        />
                    </div>
                    <div className="info-field">
                        <label>Telefono secundario:</label>
                        <input
                            type="text"
                            placeholder="N/A"
                            value={editedData.second_phone || ''}
                            onChange={(e) => setEditedData({ ...editedData, second_phone: e.target.value })}
                            disabled={!isEditMode}
                            className={isEditMode ? 'input-editable' : 'input-readonly'}
                        />
                    </div>
                    <div className="info-field">
                        <label>Email:</label>
                        <input
                            type="text"
                            placeholder="N/A"
                            value={editedData.email || ''}
                            onChange={(e) => setEditedData({ ...editedData, email: e.target.value })}
                            disabled={!isEditMode}
                            className={isEditMode ? 'input-editable' : 'input-readonly'}
                        />
                    </div>
                    <div className="info-field">
                        <label>RFC:</label>
                        <input
                            type="text"
                            placeholder="N/A"
                            value={editedData.rfc || ''}
                            onChange={(e) => setEditedData({ ...editedData, rfc: e.target.value })}
                            disabled={!isEditMode}
                            className={isEditMode ? 'input-editable' : 'input-readonly'}
                        />
                    </div>
                </div>
            </section>

            {/* Vehículos Registrados */}
            <section className="vehicles-section">
                <h3>Vehículos Registrados ({vehicles.length})</h3>
                {vehicles.length > 0 ? (
                    <GenericTable columns={columnsVehicles} data={vehicles} />
                ) : (
                    <p className="no-data">No hay vehículos registrados para este cliente.</p>
                )}
            </section>

        </div>
    );
};

export default ClientDetails;